// The provider replaces its current partial, then commits that individual segment.
// Keep both separate from the original draft so updates never search/replace text.
const startsWithClosingPunctuation = /^[,.;:!?%…\)\]\}»”’]/u;
const endsWithOpeningPunctuation = /[¿¡\(\[\{«“‘]$/u;

function joinText(left, right) {
  if (!left || !right) return left + right;
  if (/\s$/u.test(left) || /^\s/u.test(right)) return left + right;
  if (startsWithClosingPunctuation.test(right) || endsWithOpeningPunctuation.test(left)) {
    return left + right;
  }
  return `${left} ${right}`;
}

/**
 * Create one dictation run against an immutable draft and saved selection end.
 * Pass textarea.selectionEnd (also for a nonempty selection): dictation inserts
 * after selected text, preserving it. Caret offsets use textarea's UTF-16 units.
 *
 * Call commit once for each provider message. If the transport supplies a stable
 * event identity, pass it as eventId to make duplicate delivery idempotent. Never
 * deduplicate by transcript text: distinct segments can contain identical speech.
 * A manual edit must call freeze synchronously before native input proceeds, then
 * use a fresh state for the next run. Frozen states only collect late finals.
 */
export function createTranscriptState(text = '', selectionEnd) {
  const originalText = String(text ?? '');
  const offset = Number.isFinite(selectionEnd)
    ? Math.max(0, Math.min(originalText.length, Math.trunc(selectionEnd)))
    : originalText.length;
  const prefix = originalText.slice(0, offset);
  const suffix = originalText.slice(offset);
  const committedEventIds = new Set();
  let committed = '';
  let provisional = '';
  let frozenSnapshot = null;
  let recovery = '';

  function snapshot() {
    if (frozenSnapshot) {
      return { ...frozenSnapshot, recovery, frozen: true, provisionalRange: null };
    }

    const committedPrefix = joinText(prefix, committed);
    const insertionPrefix = joinText(committedPrefix, provisional);
    const hasInsertion = committed.length > 0 || provisional.length > 0;
    return {
      text: hasInsertion ? joinText(insertionPrefix, suffix) : originalText,
      caret: hasInsertion ? insertionPrefix.length : offset,
      recovery,
      frozen: false,
      // Includes a newly added leading separator, but never original draft text.
      provisionalRange: provisional
        ? { start: committedPrefix.length, end: insertionPrefix.length }
        : null,
    };
  }

  function partial(value) {
    if (!frozenSnapshot && typeof value === 'string') provisional = value;
    return snapshot();
  }

  function commit(value, eventId) {
    if (typeof value !== 'string') return snapshot();
    if (eventId !== undefined && eventId !== null) {
      if (committedEventIds.has(eventId)) return snapshot();
      committedEventIds.add(eventId);
    }

    if (frozenSnapshot) {
      if (value.trim()) recovery = joinText(recovery, value);
    } else {
      // A stop acknowledgement can contain no final wording. Keep the words
      // already shown and finalize that segment before the next partial arrives.
      committed = joinText(committed, value.trim() ? value : provisional);
      provisional = '';
    }
    return snapshot();
  }

  function freeze() {
    if (!frozenSnapshot) frozenSnapshot = snapshot();
    return snapshot();
  }

  return { partial, commit, freeze, snapshot };
}
